export type FieldType =
    | "text"
    | "email"
    | "tel"
    | "number"
    | "date"
    | "textarea"
    | "radio"
    | "checkbox"
    | "select"
    | "subheading";

export interface Option {
    label: string;
    value: string;
}

export interface Field {
    name?: string;
    label: string;
    type: FieldType;
    required?: boolean;
    placeholder?: string;
    options?: Option[];
    condition?: (data: Record<string, any>) => boolean;
    readOnly?: boolean | ((data: Record<string, any>) => boolean);
    dependsOn?: string;
    showWhen?: (value: any) => boolean;
}

export interface Section {
    layout?: string;
    title: string;
    condition?: (formData: Record<string, any>) => boolean;
    fields: Field[];
    /** 'single' renders one field per row instead of the default 3-column grid. */
    // layout?: 'grid' | 'single';
}

export const FIELD_LIMITS: Record<string, number> = {
  firstName: 100, middleName: 100, lastName: 100, gender: 20, nationality: 50, placeOfBirth: 50, religiousAffiliation: 50,
  email: 255, cellPhone: 20, physicalAddress: 255, district: 50, zipCode: 20, poBox: 20, residenceStatus: 200, otherResidenceStatus: 200,
  previousSchoolName: 255, previousSchoolPhone: 20, previousSchoolAddress: 500, lastGradeCompleted: 50,
  medicalConditionsDetails: 255, takingMedicationsDetails: 255, foodAllergiesDetails: 255, medicationAllergiesDetails: 255, specialNeedsDetails: 255, learningDisorderDetails: 255, behavioralProblemDetails: 255,
  motherFirstName: 100, motherMiddleName: 100, motherLastName: 100, motherEmail: 200, motherPhoneHome: 20, motherPhoneWork: 20, motherPhoneCell: 20, motherAddress: 200, motherPoBox: 10, motherOccupation: 100,
  fatherFirstName: 100, fatherMiddleName: 100, fatherLastName: 100, fatherEmail: 200, fatherPhoneHome: 20, fatherPhoneWork: 20, fatherPhoneCell: 20, fatherAddress: 200, fatherPoBox: 10, fatherOccupation: 100,
  guardianFirstName: 100, guardianMiddleName: 100, guardianLastName: 100, guardianEmail: 200, guardianPhoneHome: 20, guardianPhoneWork: 20, guardianPhoneCell: 20, guardianAddress: 200, guardianPoBox: 10, guardianOccupation: 100,
  studentLivesWith: 255, pickupResponsibility: 255, feeResponsibility: 255, referralSource: 255, placementSource: 255,
  emergencyFirstName: 100, emergencyMiddleName: 100, emergencyLastName: 100, emergencyEmail: 200, emergencyPhoneHome: 20, emergencyPhoneWork: 20, emergencyPhoneCell: 20, emergencyAddress: 200, emergencyPoBox: 10,
  authorizationName: 100, relationship: 50, authorizationPoBox: 10, authorizationPhoneHome: 20, authorizationPhoneWork: 20, authorizationPhoneCell: 20, authorizationEmail: 200, authorizationAddress: 200,
};

const normalizeGrade = (value?: string) =>
    value?.trim().toLowerCase().replace(/[-\s]/g, "");

export const studentFormSections: Section[] = [
    {
        title: "Student Details",
        fields: [
            { name: "firstName", label: "First Name", type: "text", required: true },
            { name: "middleName", label: "Middle Name", type: "text" },
            { name: "lastName", label: "Last Name", type: "text", required: true },
            { name: "dobDay", label: "Date of Birth", type: "date", required: true, placeholder: "YYYY-MM-DD" },
        ],
    },
    {
        title: "Basic Informations",
        fields: [
            {
                name: "gender", label: "Gender", type: "select", required: true,
                options: [
                    { label: "Male", value: "Male" },
                    { label: "Female", value: "Female" },
                ],
            },
            { name: "placeOfBirth", label: "Place of Birth", type: "text" },
            { name: "nationality", label: "Nationality", type: "text" },
            { name: "email", label: "Email Address", type: "email", required: true },
            { name: "cellPhone", label: "Cell Phone", type: "tel", required: true },
            {
                name: "religiousAffiliation", label: "Religious Affiliation", type: "select", required: true,
                options: [
                    { label: "Seventh Adventist", value: "Seventh Adventist" },
                    { label: "Other", value: "Other" },
                ]
            },
            {
                name: "otherReligiousAffiliation", label: "If Other, specify", type: "text",
                condition: (d) => d.religiousAffiliation === "Other",
            },
            {
                name: "baptized", label: "Baptized", type: "radio",
                options: [
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                ],
                condition: (d) => d.religiousAffiliation === "Seventh Adventist"
            },
            {
                name: "adventistChurchAffiliation", label: "Church Affiliation", type: "text",
                condition: (d) => d.religiousAffiliation === "Seventh Adventist"
            },
            {
                name: "churchFinancialSupport", label: "Church Financial Support", type: "radio",
                options: [
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                ],
                condition: (d) => d.religiousAffiliation === "Seventh Adventist"
            },
        ],
    },
    {
        title: "Visa Information",
        fields: [
            {
                name: "residenceStatus", label: "Residence Status", type: "select",
                options: [
                    { label: "Caymanian", value: "Caymanian" },
                    { label: "Status Holder", value: "Status Holder" },
                    { label: "Permanent Resident", value: "Permanent Resident" },
                    { label: "Child of permanent or temporary resident", value: "Child of permanent or temporary resident" },
                    { label: "Dependent of Work Permit Holder", value: "Dependent of Work Permit Holder" },
                    { label: "Other", value: "Other" },
                ],
            },
            {
                name: "otherResidenceStatus", label: "If Other, specify", type: "text",
                condition: (d) => d.residenceStatus === "Other",
            },
        ],
    },
    {
        title: "Address",
        fields: [
            { name: "physicalAddress", label: "Address", type: "text" },
            { name: "district", label: "District", type: "text" },
            { name: "poBox", label: "P.O. Box", type: "text" },
            { name: "zipCode", label: "Zip Code", type: "text" },
        ],
    },
    {
        title: "Previous School Information",
        fields: [
            { name: "previousSchoolName", label: "Name", type: "text" },
            { name: "lastGradeCompleted", label: "Last Grade Completed", type: "text" },
            { name: "previousSchoolPhone", label: "Phone Number", type: "tel" },
            { name: "previousSchoolAddress", label: "School Street Address", type: "textarea" },
        ],
    },
    {
        title: "Medical Information",
        layout: "single",
        fields: [
            {
                name: "medicalConditions", label: "Does your child have any medical conditions?", type: "radio",
                required: true,
                options: [
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                ],
            },
            {
                name: "medicalConditionsDetails", label: "Details", type: "textarea",
                condition: (d) => d.medicalConditions === "true",
                dependsOn: "medicalConditions",
                showWhen: (val) => val === "true",
                required: true,
            },
            {
                name: "takingMedications", label: "Is your child taking any medications?", type: "radio",
                required: true,
                options: [
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                ],
            },
            {
                name: "takingMedicationsDetails", label: "Details", type: "textarea",
                condition: (d) => d.takingMedications === "true",
                dependsOn: "takingMedications",
                showWhen: (val) => val === "true",
                required: true,
            },
            {
                name: "foodAllergies", label: "Does your child have any food allergies?", type: "radio",
                required: true,
                options: [
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                ],
            },
            {
                name: "foodAllergiesDetails", label: "Details", type: "textarea",
                condition: (d) => d.foodAllergies === "true",
                dependsOn: "foodAllergies",
                showWhen: (val) => val === "true",
                required: true,
            },
            {
                name: "medicationAllergies", label: "Does your child have any medication allergies?", type: "radio",
                required: true,
                options: [
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                ],
            },
            {
                name: "medicationAllergiesDetails", label: "Details", type: "textarea",
                condition: (d) => d.medicationAllergies === "true",
                dependsOn: "medicationAllergies",
                showWhen: (val) => val === "true",
                required: true,
            },
            {
                name: "specialNeeds", label: "Does your child have any special needs?", type: "radio",
                required: true,
                options: [
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                ],
            },
            {
                name: "specialNeedsDetails", label: "Details", type: "textarea",
                condition: (d) => d.specialNeeds === "true",
                dependsOn: "specialNeeds",
                showWhen: (val) => val === "true",
                required: true,
            },
            {
                name: "learningDisorder", label: "Does your child suffer from any known learning disorder?", type: "radio",
                required: true,
                options: [
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                ],
            },
            {
                name: "learningDisorderDetails", label: "Details", type: "textarea",
                condition: (d) => d.learningDisorder === "true",
                dependsOn: "learningDisorder",
                showWhen: (val) => val === "true",
                required: true,
            },
            {
                name: "behavioralProblem", label: "Does your child have any known behavioral problem?", type: "radio",
                required: true,
                options: [
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                ],
            },
            {
                name: "behavioralProblemDetails", label: "Details", type: "textarea",
                condition: (d) => d.behavioralProblem === "true",
                dependsOn: "behavioralProblem",
                showWhen: (val) => val === "true",
                required: true,
            },
        ],
    },
];

export const parentFormSection: Section[] = [
    {
        title: "Mother",
        fields: [
            { name: "motherFirstName", label: "First Name", type: "text", required: true },
            { name: "motherMiddleName", label: "Middle Name", type: "text" },
            { name: "motherLastName", label: "Last Name", type: "text", required: true },
            { name: "motherEmail", label: "E-mail", type: "email", required: true },
            { name: "motherPhoneHome", label: "Phone (Home)", type: "tel", required: true },
            { name: "motherPhoneWork", label: "Phone (Work)", type: "tel" },
            { name: "motherPhoneCell", label: "Phone (Cell)", type: "tel" },
            { name: "motherAddress", label: "Address", type: "text" },
            { name: "motherPoBox", label: "P.O. Box", type: "text" },
            { name: "motherOccupation", label: "Occupation", type: "text" },
            { name: "motherIsEmergency", label: "Is Emergency Contact", type: "checkbox" },
            { name: "motherIsContract", label: "Is Financial Contract", type: "checkbox" },
        ],
    },
    {
        title: "Father",
        fields: [
            { name: "fatherFirstName", label: "First Name", type: "text", required: true },
            { name: "fatherMiddleName", label: "Middle Name", type: "text" },
            { name: "fatherLastName", label: "Last Name", type: "text", required: true },
            { name: "fatherEmail", label: "E-mail", type: "email", required: true },
            { name: "fatherPhoneHome", label: "Phone (Home)", type: "tel", required: true },
            { name: "fatherPhoneWork", label: "Phone (Work)", type: "tel" },
            { name: "fatherPhoneCell", label: "Phone (Cell)", type: "tel" },
            { name: "fatherAddress", label: "Address", type: "text" },
            { name: "fatherPoBox", label: "P.O. Box", type: "text" },
            { name: "fatherOccupation", label: "Occupation", type: "text" },
            { name: "fatherIsEmergency", label: "Is Emergency Contact", type: "checkbox" },
            { name: "fatherIsContract", label: "Is Financial Contract", type: "checkbox" },
        ],
    },
    {
        title: "Guardian (If Applicable)",
        fields: [
            { name: "guardianFirstName", label: "First Name", type: "text" },
            { name: "guardianMiddleName", label: "Middle Name", type: "text" },
            { name: "guardianLastName", label: "Last Name", type: "text" },
            { name: "guardianEmail", label: "E-mail", type: "email" },
            { name: "guardianPhoneHome", label: "Phone (Home)", type: "tel" },
            { name: "guardianPhoneWork", label: "Phone (Work)", type: "tel" },
            { name: "guardianPhoneCell", label: "Phone (Cell)", type: "tel" },
            { name: "guardianAddress", label: "Address", type: "text" },
            { name: "guardianPoBox", label: "P.O. Box", type: "text" },
            { name: "guardianOccupation", label: "Occupation", type: "text" },
            { name: "guardianIsEmergency", label: "Is Emergency Contact", type: "checkbox" },
            { name: "guardianIsContract", label: "Is Financial Contract", type: "checkbox" },
        ],
    },
    {
        title: "Additional Information",
        layout: "single",
        fields: [
            { name: "studentLivesWith", label: "Who does student live with?", type: "text" },
            { name: "pickupResponsibility", label: "Who is responsible for picking up student at dismissal?", type: "text" },
            { name: "feeResponsibility", label: "Who is responsible for paying student's school fee?", type: "text" },
            { name: "referralSource", label: "Who recommended you to Cayman Academy?", type: "text" },
            { name: "placementSource", label: "Which other schools have you applied to for placement?", type: "text" },
        ],
    },
];

const isEmergencyLocked = (data: Record<string, any>) =>
    !!(data.motherIsEmergency || data.fatherIsEmergency || data.guardianIsEmergency);

const isContractLocked = (data: Record<string, any>) =>
    !!(data.motherIsContract || data.fatherIsContract || data.guardianIsContract);

export const financialAgreementSection: Section[] = [
    {
        title: "Emergency Contact",
        fields: [
            { name: "emergencyFirstName", label: "First Name", type: "text", required: true, readOnly: isEmergencyLocked },
            { name: "emergencyMiddleName", label: "Middle Name", type: "text", readOnly: isEmergencyLocked },
            { name: "emergencyLastName", label: "Last Name", type: "text", required: true, readOnly: isEmergencyLocked },
            { name: "emergencyEmail", label: "E-mail", type: "email", required: true, readOnly: isEmergencyLocked },
            { name: "emergencyPhoneHome", label: "Phone (Home)", type: "tel", required: true, readOnly: isEmergencyLocked },
            { name: "emergencyPhoneWork", label: "Phone (Work)", type: "tel", readOnly: isEmergencyLocked },
            { name: "emergencyPhoneCell", label: "Phone (Cell)", type: "tel", readOnly: isEmergencyLocked },
            { name: "emergencyAddress", label: "Address", type: "text", readOnly: isEmergencyLocked },
            { name: "emergencyPoBox", label: "P.O. Box", type: "text", readOnly: isEmergencyLocked },
        ],
    },
    {
        title: "Financial Contract",
        fields: [
            { name: "authorizationName", label: "Authorization Name", type: "text", required: true, readOnly: isContractLocked },
            { name: "relationship", label: "Relationship", type: "text", readOnly: isContractLocked },
            { name: "authorizationPoBox", label: "P.O. Box", type: "text", readOnly: isContractLocked },
            { name: "authorizationPhoneHome", label: "Phone (Home)", type: "tel", required: true, readOnly: isContractLocked },
            { name: "authorizationPhoneWork", label: "Phone (Work)", type: "tel", readOnly: isContractLocked },
            { name: "authorizationPhoneCell", label: "Phone (Cell)", type: "tel", readOnly: isContractLocked },
            { name: "authorizationEmail", label: "E-mail", type: "email", required: true, readOnly: isContractLocked },
            { name: "authorizationAddress", label: "Address", type: "text", readOnly: isContractLocked },
        ],
    },
];
